import type {NormalizedCacheObject} from '@apollo/client';
import type {Mapper} from '@automapper/core';
import type {CustomErrorCodesLocalizationKeys} from '@error-handling';
import {customErrorCodesLocalization} from '@error-handling';
import type {ApolloClientProxy} from '@igp/shared/api';
import type {IUserManagerExtended} from '@igp/shared/auth';
import {ActionBulkEpicsBuilder} from '@igp/shared/features/bulk-operation';
import {P2PTransferTemplateFactory, TransactionDownloadEpicsBuilder, TransactionService} from '@igp/shared/features/transaction';
import {UserProfileService} from '@igp/shared/features/user-profile';
import type {CsvDocumentBuilderAdapter, FileDownloadService} from '@igp/shared/file';
import {MessageLocalizer} from '@igp/shared/localization';
import type {IEpicsBuilder} from '@igp/shared/redux';
import {BaseEpicsBuilder} from '@igp/shared/redux';
import {AsyncActionEpicsBuilder} from '@igp/shared/redux/async-actions';
import type {IEntityReadService} from '@igp/shared/redux/entity';
import {EntityEpicsBuilder, EntityReadServiceFactorySymbol} from '@igp/shared/redux/entity';
import {FileEpicsBuilder} from '@igp/shared/redux/file';
import type {IRealtimeService} from '@igp/shared/redux/realtime';
import {RealtimeEpicsBuilder, RealtimeServiceSymbol} from '@igp/shared/redux/realtime';
import {ViewEpicsBuilder} from '@igp/shared/redux/view';
import {EntityType, entityTypes, schemaMapper} from '@models';
import type {Container} from 'inversify';
import type {Epic} from 'redux-observable';
import type {RootAction, RootState} from 'typesafe-actions';

import {ServiceTypes} from '@ioc-container';
import {boUserServiceTypes} from '@features/bo-user';
import {DebtResponsibleMasterAgentsServiceTypes} from '@features/debt-responsible-master-agent';
import {DebtTransferLogServiceTypes} from '@features/debt-transfer-log';
import {ReferralLinkConfigServiceTypes} from '@features/referral-link-config';
import {RepaymentLogServiceTypes} from '@features/repayment-log';

import actions from './rootActions';

class RootEpic extends BaseEpicsBuilder {
    private readonly _container: Container;
    constructor(container: Container) {
        super();
        this._container = container;
    }
    protected buildEpicList(): Epic[] {
        const messageLocalizer = new MessageLocalizer<CustomErrorCodesLocalizationKeys>(customErrorCodesLocalization);
        const apolloClient = this._container.get<ApolloClientProxy<NormalizedCacheObject>>(ServiceTypes.ApolloClientIGP);
        const csvDocumentBuilderAdapter = this._container.get<CsvDocumentBuilderAdapter<string>>(ServiceTypes.CsvFileAdapter);
        const autoMapper = this._container.get<Mapper>(ServiceTypes.AutoMapper);
        const downloadService = this._container.get<FileDownloadService>(ServiceTypes.FileDownloadService);
        const userManager = this._container.get<IUserManagerExtended>(ServiceTypes.UserManager);

        return [
            new ViewEpicsBuilder<EntityType>(schemaMapper, entityTypes).buildEpics(),
            new EntityEpicsBuilder<EntityType>(
                this._container.get<(entityType: EntityType) => IEntityReadService<EntityType>>(EntityReadServiceFactorySymbol),
                entityTypes,
                schemaMapper
            ).buildEpics(),
            new RealtimeEpicsBuilder<EntityType>(
                this._container.get<IRealtimeService<EntityType>>(RealtimeServiceSymbol),
                this._container.get<(entityType: EntityType) => IEntityReadService<EntityType>>(EntityReadServiceFactorySymbol),
                schemaMapper
            ).buildEpics(),
            new TransactionDownloadEpicsBuilder(
                new TransactionService(apolloClient, messageLocalizer),
                csvDocumentBuilderAdapter,
                new P2PTransferTemplateFactory(),
                autoMapper,
                new UserProfileService<CustomErrorCodesLocalizationKeys>(apolloClient, messageLocalizer, autoMapper)
            ).buildEpics(),
            new FileEpicsBuilder(downloadService).buildEpics(),
            this._container.get<BaseEpicsBuilder>(ServiceTypes.DonwloadEpicsBuilder).buildEpics(),
            new AsyncActionEpicsBuilder(actions, userManager).buildEpics(),
            this._container.get<IEpicsBuilder>(ServiceTypes.UserProfileEpicsBuilder).buildEpics(),
            new ActionBulkEpicsBuilder<EntityType>(
                this._container.get<IEntityReadService<EntityType>>(ServiceTypes.BulkOperationService),
                EntityType.BulkOperation
            ).buildEpics(),
            this._container.get<IEpicsBuilder>(ServiceTypes.MessageEpicsBuilder).buildEpics(),
            this._container.get<IEpicsBuilder>(DebtResponsibleMasterAgentsServiceTypes.epicsBuilder).buildEpics(),
            this._container.get<IEpicsBuilder>(RepaymentLogServiceTypes.epicsBuilder).buildEpics(),
            this._container.get<IEpicsBuilder>(boUserServiceTypes.epicsBuilder).buildEpics(),
            this._container.get<IEpicsBuilder>(ReferralLinkConfigServiceTypes.epicsBuilder).buildEpics(),
            this._container.get<IEpicsBuilder>(ServiceTypes.PlayerReferralEpicsBuidler).buildEpics(),
            this._container.get<IEpicsBuilder>(DebtTransferLogServiceTypes.epicsBuilder).buildEpics(),
        ];
    }
}

export function initEpics(container: Container): Epic<RootAction, RootAction, RootState> {
    return new RootEpic(container).buildEpics() as Epic<RootAction, RootAction, RootState>;
}
